import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, MapPin, ShieldCheck, Zap } from "lucide-react";
import { lineReveal } from "@/lib/motion";
import { IMAGES } from "@/lib/content";

const STAGES = [
  { l: "H", t: "Hear" }, { l: "A", t: "Activate" }, { l: "T", t: "Team Up" },
  { l: "C", t: "Care & Comply" }, { l: "H", t: "Help You Scale" },
];

// scattered decorative pixels
const PIXELS = [
  { top: "14%", left: "8%", s: 10, d: 0 }, { top: "72%", left: "5%", s: 14, d: 1.2 },
  { top: "30%", left: "46%", s: 8, d: 0.6 }, { top: "84%", left: "40%", s: 10, d: 1.8 },
  { top: "18%", left: "92%", s: 12, d: 0.9 }, { top: "60%", left: "88%", s: 8, d: 2.1 },
];

export const Hero = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 600], [0, 110]);
  const y2 = useTransform(scrollY, [0, 600], [0, -70]);
  const yImg = useTransform(scrollY, [0, 600], [0, 60]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % STAGES.length), 1400);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative min-h-[100svh] overflow-hidden pt-[72px]" data-testid="hero">
      {/* animated mesh + grid */}
      <div className="hero-mesh animate-mesh absolute inset-0" />
      <motion.div style={{ y: y1 }} className="hatch-bg hatch-drift absolute inset-0 opacity-50" />
      <div className="hatch-diag absolute inset-0 opacity-30" />
      <motion.div style={{ y: y2 }} className="absolute -right-32 top-10 h-[540px] w-[540px] rounded-full bg-orange-500/10 blur-[130px]" />
      <div className="absolute -left-40 bottom-0 h-[440px] w-[440px] rounded-full bg-amber-500/10 blur-[130px]" />

      {/* floating pixels */}
      {PIXELS.map((p, i) => (
        <motion.span
          key={i}
          className="absolute hidden rounded-[3px] bg-gradient-to-br from-orange-400/70 to-amber-500/40 lg:block"
          style={{ top: p.top, left: p.left, width: p.s, height: p.s }}
          animate={{ y: [0, -18, 0], opacity: [0.35, 0.9, 0.35] }}
          transition={{ duration: 5 + p.d, repeat: Infinity, ease: "easeInOut", delay: p.d }}
        />
      ))}

      <div className="container-px relative flex min-h-[calc(100svh-72px)] flex-col justify-start pt-8 pb-16 lg:justify-center lg:pt-12">
        <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          {/* LEFT */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="mb-7 inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/5 px-4 py-1.5 backdrop-blur-sm"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-orange-400" />
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-orange-300">India Expansion & GCC Partner</span>
            </motion.div>

            <h1 className="font-head text-[2.6rem] font-black leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-[4.4rem]">
              <span className="reveal-mask block">
                <motion.span variants={lineReveal} custom={0} initial="hidden" animate="show" className="block">
                  Build Your
                </motion.span>
              </span>
              <span className="reveal-mask block pb-2">
                <motion.span variants={lineReveal} custom={1} initial="hidden" animate="show" className="block">
                  <span className="grad-text">India Team.</span>
                </motion.span>
              </span>
              <span className="reveal-mask block">
                <motion.span variants={lineReveal} custom={2} initial="hidden" animate="show" className="block text-zinc-300">
                  We'll Handle the Rest.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7 }}
              className="mt-7 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
            >
              We help startups and growing global businesses establish, operate, and scale teams in
              India — from market entry and entity setup to hiring, HR, payroll, compliance, finance,
              IT, and ongoing operations.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="mt-9 flex flex-col gap-4 sm:flex-row"
            >
              <button onClick={() => navigate("/contact")} className="btn-primary" data-testid="hero-cta-consultation">
                Book a Free Consultation <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => navigate("/services")} className="btn-ghost" data-testid="hero-cta-services">
                Explore Our Services
              </button>
            </motion.div>

            {/* value chips */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="mt-10 grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-3"
            >
              {[
                { icon: Zap, t: "Faster setup", s: "Live in weeks, not months" },
                { icon: ShieldCheck, t: "Fully compliant", s: "HR, payroll & legal handled" },
                { icon: MapPin, t: "Noida-based", s: "Real local expertise" },
              ].map((c, i) => (
                <div key={i} className="rounded-xl border border-white/8 bg-white/[0.02] p-3.5 backdrop-blur-sm">
                  <c.icon className="mb-1.5 h-4 w-4 text-orange-400" />
                  <div className="text-sm font-semibold text-white">{c.t}</div>
                  <div className="text-xs text-zinc-500">{c.s}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* RIGHT visual */}
          <motion.div style={{ opacity }} className="relative hidden lg:block">
            <motion.div style={{ y: yImg }} className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.92, rotate: 1 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="clip-frame relative overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
              >
                <img src={IMAGES.collaboration} alt="India team building at Pixel-Hatch" className="h-[460px] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E10] via-[#0E0E10]/20 to-transparent" />
                {/* spotlight */}
                <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_70%_20%,rgba(251,146,60,0.18),transparent)]" />
              </motion.div>

              {/* floating HATCH card */}
              <motion.div
                initial={{ opacity: 0, x: -30, y: 20 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ delay: 0.9, duration: 0.7 }}
                className="animate-floaty absolute -left-10 bottom-16 w-64 glass rounded-2xl p-5"
              >
                <div className="kicker mb-3">The HATCH Framework™</div>
                <div className="flex items-center gap-1.5">
                  {STAGES.map((s, i) => (
                    <div key={i} className="flex flex-1 flex-col items-center gap-1">
                      <div
                        className={`flex h-9 w-9 items-center justify-center rounded-lg font-head text-sm font-black transition-all duration-300 ${
                          active === i ? "grad-accent scale-110 text-[#1a0f00]" : "bg-white/5 text-zinc-500"
                        }`}
                      >
                        {s.l}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/10">
                  <motion.div className="h-full grad-accent" animate={{ width: `${((active + 1) / STAGES.length) * 100}%` }} transition={{ duration: 0.4 }} />
                </div>
                <p className="mt-3 text-xs text-zinc-400">
                  <span className="font-semibold text-orange-300">{STAGES[active].t}</span> — from vision to victory.
                </p>
              </motion.div>

              {/* floating badge */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.7 }}
                className="absolute -right-6 top-10 glass rounded-2xl px-5 py-4"
              >
                <div className="font-head text-2xl font-black grad-text">End-to-End</div>
                <div className="text-xs text-zinc-400">one accountable partner</div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
